
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Camera, X, RotateCcw, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface PhotoCaptureProps {
  onPhotoCapture: (photoUrl: string) => void;
  onClose: () => void;
}

export const PhotoCapture = ({ onPhotoCapture, onClose }: PhotoCaptureProps) => {
  const [cameraState, setCameraState] = useState<'idle' | 'starting' | 'active' | 'error'>('idle');
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const { toast } = useToast();

  const startCamera = async () => {
    console.log('Starting camera...');
    setCameraState('starting');
    setErrorMessage('');
    
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Camera not supported");
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });

      console.log('Got camera stream');
      streamRef.current = stream;

      if (videoRef.current && streamRef.current) {
        videoRef.current.srcObject = streamRef.current;
        
        await new Promise((resolve, reject) => {
          if (!videoRef.current) return reject('No video element');
          
          videoRef.current.onloadedmetadata = () => {
            console.log('Video loaded, playing...');
            videoRef.current?.play()
              .then(() => {
                console.log('Video playing successfully');
                setCameraState('active');
                resolve(true);
              })
              .catch(reject);
          };
          
          videoRef.current.onerror = reject;
        });

        toast({
          title: "Camera Ready",
          description: "Position your product and tap capture.",
        });
      }
    } catch (error: any) {
      console.error('Camera error:', error);
      setCameraState('error');
      
      let message = 'Camera access failed';
      if (error.name === 'NotAllowedError') {
        message = 'Camera permission denied. Please allow camera access.';
      } else if (error.name === 'NotFoundError') {
        message = 'No camera found.';
      }
      
      setErrorMessage(message);
      stopCamera();
    }
  };

  const stopCamera = () => {
    console.log('Stopping camera...');
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setCameraState('idle');
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current || cameraState !== 'active') {
      toast({
        title: "Camera Not Ready",
        description: "Please wait for camera to start.",
        variant: "destructive"
      });
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    context.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    
    if (dataUrl && dataUrl !== 'data:,') {
      setCapturedPhoto(dataUrl);
      stopCamera();
      toast({
        title: "Photo Captured!",
        description: "Photo captured successfully.",
      });
    }
  };

  const savePhoto = () => {
    if (capturedPhoto) {
      onPhotoCapture(capturedPhoto);
      onClose();
    }
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
    setErrorMessage('');
    startCamera();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log('Component unmounting, cleaning up...');
      stopCamera();
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Take Photo</h3>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          {errorMessage && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          )}
          
          {capturedPhoto ? (
            <div className="space-y-4">
              <div className="relative">
                <img
                  src={capturedPhoto}
                  alt="Captured product"
                  className="w-full rounded-lg"
                  style={{ maxHeight: '300px', objectFit: 'contain' }}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={retakePhoto} variant="outline" className="flex-1">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Retake
                </Button>
                <Button onClick={savePhoto} className="flex-1">
                  Save Photo
                </Button>
              </div>
            </div>
          ) : cameraState === 'idle' || cameraState === 'error' ? (
            <div className="text-center space-y-4">
              <Camera className="w-16 h-16 mx-auto text-gray-400" />
              <p className="text-gray-600">Ready to take product photos</p>
              <Button onClick={startCamera} className="w-full">
                <Camera className="w-4 h-4 mr-2" />
                Start Camera
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '4/3' }}>
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  playsInline
                />
                {cameraState === 'starting' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="text-white text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                      <p>Starting camera...</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={capturePhoto} 
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={cameraState !== 'active'}
                >
                  <Camera className="w-4 h-4 mr-2" />
                  {cameraState === 'active' ? "Capture Photo" : "Loading..."}
                </Button>
                <Button onClick={stopCamera} variant="outline" className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          )}
          
          <canvas ref={canvasRef} className="hidden" />
        </CardContent>
      </Card>
    </div>
  );
};
